/* eslint-disable no-mixed-operators */
import React, { useState, useEffect } from 'react'
import useNotificationsApi from '../../hooks/notifications/useNotificationsApi'
import Button from '@material-ui/core/Button'
import { useIsSecurityUser } from '../../hooks/useIsSecurityUser'
import CustomTable from '../customTable/CustomTable'
import ShowImageTable from '../common/ShowImageTable'
import ListHeader from '../common/ListHeader'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useGetUserInfo } from '../../hooks/useGetUserInfo'
import moment from 'moment-timezone'
import { userTypes } from '../../utils/constants'
import { paths } from '../../utils/paths'
import { useNotification } from '../../hooks/notifications/notificationContext'

export default function NotificationsList({ building, shopping = false }) {
  const isSecurityUser = useIsSecurityUser()
  const history = useHistory()
  const userInfo = useGetUserInfo()

  const {
    state: { notificationsState },
    methods: {
      getNotifications,
      updateNotification,
      getServiceTypes,
      getNotificationsToOpen,
    },
  } = useNotification()

  useEffect(() => {
    const fetchServiceTypes = () => getServiceTypes({ shopping })
    fetchServiceTypes()
  }, [])

  const columns = [
    {
      id: 'confirm',
      label: '',
      minWidth: 130,
      format: (communication) =>
        !communication.confirmed ? (
          <Button
            variant="contained"
            color="primary"
            style={{ width: '130px' }}
            onClick={() => {
              updateNotification(
                communication?.id,
                {
                  confirmed: true,
                  receivedAt: new Date().toISOString(),
                  receivedBy: userInfo?.id,
                },
                {
                  limit: 10,
                  offset: 0,
                  ...(building && { buildingId: building.id }),
                  shopping,
                  showHistory: true,
                }
              )
            }}
          >
            {shopping ? 'Recibido' : 'Notificar arribo'}
          </Button>
        ) : (
          <Button
            color="green"
            disabled
            variant="contained"
            style={{ width: '130px' }}
          >
            {shopping ? 'Recibido' : 'Notificado'}
          </Button>
        ),
    },
    ...(shopping
      ? [
          {
            id: 'deliver',
            label: '',
            minWidth: 130,
            format: (communication) => (
              <Button
                variant="contained"
                color="primary"
                style={{ width: '130px' }}
                disabled={communication.deliveredAt}
                onClick={() => {
                  updateNotification(
                    communication?.id,
                    {
                      deliveredAt: new Date().toISOString(),
                      deliveredBy: userInfo?.id,
                    },
                    {
                      limit: 10,
                      offset: 0,
                      ...(building && { buildingId: building.id }),
                      shopping,
                      showHistory: true,
                    }
                  )
                }}
              >
                Entregado
              </Button>
            ),
          },
        ]
      : []),
    {
      id: 'image',
      label: 'Persona',
      minWidth: 250,
      format: (communication) =>
        communication?.user ? (
          <ShowImageTable
            imageUrl={communication?.user?.imageUrl}
            name={`${communication?.user?.name} ${communication?.user?.surname}`}
            ci={communication?.user?.email}
          ></ShowImageTable>
        ) : (
          'General'
        ),
    },
    {
      id: 'type',
      label: 'Tipo',
      minWidth: 100,
      format: (value) => formatUnderscore(value.type),
    },
    {
      id: 'unidad',
      label: isSecurityUser ? 'Unidad' : 'Cliente/Unidad',
      minWidth: 100,
      format: (notification) => {
        if (notification.user && notification.user.apartments.length > 0) {
          const apartment = notification?.user?.apartments[0]
          return isSecurityUser
            ? apartment?.name
            : `${apartment?.building?.name}/${apartment?.name}`
        } else {
          return isSecurityUser
            ? notification.apartment?.name
            : `${notification.apartment?.building?.name}/${notification.apartment?.name}`
        }
      },
    },
    // {
    //   id: 'user',
    //   label: 'Residente',
    //   minWidth: 170,
    //   format: (communication) =>
    //     `${communication?.user?.name} ${communication?.user?.surname}`,
    // },
    { id: 'observations', label: 'Obs.', minWidth: 170 },
    {
      id: 'willArriveBy',
      label: 'Posible arribo',
      minWidth: 170,
      format: (value) =>
        value.startTime && value.endTime
          ? moment(value.startTime).isSame(moment(value.endTime), 'day')
            ? moment(value.startTime).format('DD/MM/YYYY')
            : `${moment(value.startTime).format('DD/MM/YYYY')} -
              ${moment(value.endTime).format('DD/MM/YYYY')}`
          : 'No indicado',
    },
    {
      id: 'receivedAt',
      label: shopping ? 'Recibido' : 'Notificación de arribo',
      minWidth: 170,
      format: (value) =>
        value.receivedAt
          ? new Date(value.receivedAt).toLocaleString()
          : shopping
          ? 'No recibido'
          : 'Sin notificar',
    },
    {
      id: 'receivedBy',
      label: shopping ? 'Recibido por' : 'Quien notifica',
      minWidth: 170,
      format: (value) =>
        value.receivedBy
          ? `${value.receivedByUser.name} ${value.receivedByUser.surname}`
          : 'No recibido',
    },
    ...(shopping
      ? [
          {
            id: 'deliveredAt',
            label: 'Entregado',
            minWidth: 170,
            format: (value) =>
              value.deliveredAt
                ? new Date(value.deliveredAt).toLocaleString()
                : 'No entregado',
          },
        ]
      : []),
    ...(shopping
      ? [
          {
            id: 'deliveredBy',
            label: 'Entregado por',
            minWidth: 170,
            format: (value) =>
              value.deliveredBy
                ? `${value.deliveredByUser.name} ${value.deliveredByUser.surname}`
                : 'No entregado',
          },
        ]
      : []),
    {
      id: 'createdAt',
      label: 'Creacion',
      minWidth: 170,
      format: (value) => new Date(value.createdAt).toLocaleString(),
    },
  ]

  const formatUnderscore = (value) => {
    return (
      value?.replaceAll('_', ' ').toUpperCase().charAt(0) +
      value?.replaceAll('_', ' ').slice(1)
    )
  }

  const communications = notificationsState?.notifications?.data || []

  const serviceTypeOptions = notificationsState?.serviceTypes
    ? [
        { label: 'Todos', value: 'all' },
        ...notificationsState?.serviceTypes?.map((type) => ({
          label: formatUnderscore(type.name),
          value: type.id,
        })),
      ]
    : [{ label: 'Todos', value: 'all' }]
  console.log(isSecurityUser, userInfo?.type)
  return (
    <>
      <ListHeader
        bigTitle={
          shopping
            ? 'Recepción de cadetería'
            : 'Notificación de arribo de servicios'
        }
        createText="Notificar arribo"
        createRedirect={() =>
          history.push('/nueva-compra', {
            shopping,
            pathOverride: shopping ? paths.SHOPPING : paths.NOTIFICATIONS,
          })
        }
        canCreate={isSecurityUser || userInfo?.type === userTypes.admin}
        noIcon={true}
      />
      <CustomTable
        loading={notificationsState.isLoading}
        columns={columns}
        data={communications || []}
        getData={async (params) => {
          await getNotifications({
            ...params,
            type: params.type === 'all' ? null : params.type,
            shopping,
            showHistory: true,
            shouldOpenClosedNotifications: true,
          })
          await getNotificationsToOpen()
        }}
        buildingFilter={!building}
        building={building}
        apartmentFilter={true}
        searchTitle={'Residente u observación'}
        searchPlaceholder={'Ingrese un residente u obs.'}
        serverPagination={true}
        serverCount={notificationsState?.notifications?.count}
        subRowField={'invitations'}
        customFilters={[
          {
            label: 'Tipo',
            type: 'select',
            field: 'type',
            options: serviceTypeOptions,
          },
        ]}
      ></CustomTable>
    </>
  )
}
